const EPOCH_MS_THRESHOLD = 1e12;
const EPOCH_US_THRESHOLD = 1e15;

const HAS_TIMEZONE_SUFFIX_RE = /[zZ]$|[+-]\d{2}:?\d{2}$/;

const ISO_LOCAL_DT_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/;

function toValidDate(ms: number): Date | null {
  const d = new Date(ms);
  return isNaN(d.getTime()) ? null : d;
}

function fractionalMs(frac: string | undefined): number {
  if (!frac) return 0;
  return parseInt((frac + "000").slice(0, 3), 10) || 0;
}

export function epochMillisFromNumeric(n: number): number | null {
  if (!Number.isFinite(n)) return null;
  const abs = Math.abs(n);
  if (abs >= EPOCH_US_THRESHOLD) return Math.trunc(n / 1000);
  if (abs >= EPOCH_MS_THRESHOLD) return Math.trunc(n);
  return Math.trunc(n * 1000);
}

function parseIsoAsUtc(s: string): Date | null {
  const trimmed = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split("-").map(Number);
    return toValidDate(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
  }

  const iso = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");

  if (HAS_TIMEZONE_SUFFIX_RE.test(iso)) {
    const normalized = iso.replace(/z$/, "Z");
    return toValidDate(new Date(normalized).getTime());
  }

  const m = iso.match(ISO_LOCAL_DT_RE);
  if (m) {
    return toValidDate(
      Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6], fractionalMs(m[7]))
    );
  }

  return toValidDate(new Date(`${iso}Z`).getTime());
}

export function parseUtcDateTime(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  if (typeof value === "number") {
    const ms = epochMillisFromNumeric(value);
    return ms == null ? null : toValidDate(ms);
  }

  if (typeof value === "string") {
    const s = value.trim();
    if (/^\d+$/.test(s)) {
      const ms = epochMillisFromNumeric(Number(s));
      return ms == null ? null : toValidDate(ms);
    }
    return parseIsoAsUtc(s);
  }

  return null;
}

const PLOTLY_AXIS_DATE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/;

/**
 * Parses a value taken from a Plotly relayout event (`xaxis.range[0]` etc).
 *
 * Plotly shifts JS `Date` inputs by the browser's timezone offset before
 * storing them, so the axis renders — and reports back — wall clock time in
 * the browser's timezone. Reading those values as UTC offsets the selected
 * range by the timezone offset, so they must be parsed as local time.
 */
export function parsePlotlyAxisDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  // Plotly emits numbers for non-date axes and for dates outside its string
  // range; those are milliseconds in the same timezone shifted space.
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return toValidDate(value + new Date(value).getTimezoneOffset() * 60000);
  }

  if (typeof value !== "string") return null;

  const m = value.trim().match(PLOTLY_AXIS_DATE_RE);
  if (!m) return null;

  return toValidDate(
    new Date(
      +m[1],
      +m[2] - 1,
      +m[3],
      m[4] ? +m[4] : 0,
      m[5] ? +m[5] : 0,
      m[6] ? +m[6] : 0,
      fractionalMs(m[7])
    ).getTime()
  );
}

export function formatUtcDateTime(value: unknown, locale = "en-GB"): string {
  const d = parseUtcDateTime(value);
  return d ? d.toLocaleString(locale) : String(value ?? "-");
}
